import { keyframes } from '@emotion/react';
import { useCallback, useEffect, useState } from 'react';
import { Flex, Text } from '../../../2022-component-library';
import { FixationTargetVF2 } from '../../../components/VisualFieldTwo/FixationTargetVF2';
import { StimulusPointVF2 } from '../../../components/VisualFieldTwo/StimulusPointVF2';
import { useCalibration } from '../../../context/calibrationContext';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useVisualFieldData } from '../../../helpers/reducers';
import { usePatientResponse } from '../../../helpers/visual-field-two-hooks/usePatientResponse';
import { useStimulusCycle } from '../../../helpers/visual-field-two-hooks/useStimulusCycle';

const fadeIn = keyframes`
0% {
  opacity: 0
}

100% {
  opacity: 1
}`;

const VisualFieldTwo = () => {
  const [testConfig, setTestConfig] = useState({});
  const [testIsRunning, setTestIsRunning] = useState(false);
  const receivedMessage = useReceiveMessage();
  const postMessage = usePostMessage();
  const { showStimulus, setShowStimulus, getDot } = useStimulusCycle();
  const { markDotSeen, markDotMissed } = usePatientResponse();
  const { updateCalibrationData } = useCalibration();
  const {
    updateTestProgressData,
    batchMultipleUpdates,
    testProgressData: {
      dotsForDisplay,
      seenDots,
      missedOnceDots,
      activeDot,
      fixationTargetCoordinates,
      stimulusCoordinates,
      fixationMoving,
      readyForNextStimulus,
      patientHasResponded,
      testEnded,
      repeatThreshold,
      dotsBetweenRepeats,
      dotsSinceLastRepeat,
      totalRepeats,
    },
  } = useVisualFieldData();

  const processPatientClick = () => {
    const illegalResponse = fixationMoving || !activeDot || patientHasResponded;
    if (illegalResponse) return null;
    return postMessage({ type: 'useVisualFieldReducer', action: 'setPatientHasResponded', payload: true });
  };

  const handlePatientResponse = useCallback(() => {
    if (patientHasResponded) markDotSeen({ activeDot, missedOnceDots });
    if (!patientHasResponded && activeDot) markDotMissed({ activeDot, missedOnceDots, seenDots });
  }, [patientHasResponded, markDotSeen, markDotMissed, activeDot, seenDots, missedOnceDots]);

  const handleNextDot = useCallback(() => {
    const shouldRepeatSeenDot = seenDots.length >= repeatThreshold && totalRepeats < 5 && dotsSinceLastRepeat === dotsBetweenRepeats;

    if (shouldRepeatSeenDot) return getDot({ dotsForDisplay, seenDots, getSeenDot: true });
    return getDot({ dotsForDisplay, seenDots });
  }, [getDot, dotsForDisplay, seenDots, dotsBetweenRepeats, dotsSinceLastRepeat, repeatThreshold, totalRepeats]);

  useEffect(() => {
    //handles some particular calibration values for which there is no shared reducer/store/context
    if (receivedMessage.type === 'calibrationDataFromOptician') {
      const { blindSpotVanishingXDistancePx, circleDimensionsPx, contrastType, fixationType, targetDuration } = receivedMessage.content;

      updateCalibrationData({ blindSpotVanishingXDistancePx, circleDimensionsPx });
      setTestConfig({ contrastType, fixationType, targetDuration });
    }
  }, [receivedMessage, updateCalibrationData]);

  useEffect(() => {
    //this receives "reducer" messages and dispatches the appropriate action.
    if (receivedMessage.type === 'useVisualFieldReducer') {
      updateTestProgressData({ type: receivedMessage.action, payload: receivedMessage.payload });
    }
  }, [receivedMessage, updateTestProgressData]);

  useEffect(() => {
    //this handles start/stop messages from optician
    if (receivedMessage.type === 'startTest') return setTestIsRunning(true);
    if (receivedMessage.type === 'stopTest' || testEnded) return setTestIsRunning(false);
  }, [receivedMessage, testEnded]);

  useEffect(() => {
    if (fixationMoving) {
      const reportTransitionFinished = setTimeout(() => {
        return batchMultipleUpdates({ setFixationMoving: false, setReadyForNextStimulus: true });
      }, 2000);
      return () => clearTimeout(reportTransitionFinished);
    }
  }, [fixationMoving, batchMultipleUpdates]);

  useEffect(() => {
    if (testIsRunning) return postMessage({ type: 'beginStimulusCycle' });
    if (!testIsRunning) return setShowStimulus(false);
  }, [testIsRunning, postMessage, setShowStimulus]);

  useEffect(() => {
    if (testIsRunning && receivedMessage.type === 'beginStimulusCycle') handleNextDot();
  }, [testIsRunning, receivedMessage, handleNextDot]);

  useEffect(() => {
    if (readyForNextStimulus) setShowStimulus(true);
  }, [setShowStimulus, readyForNextStimulus, activeDot]);

  useEffect(() => {
    if (testIsRunning && receivedMessage.type === 'endStimulusCycle') {
      return handlePatientResponse();
    }
  }, [testIsRunning, receivedMessage, postMessage, handlePatientResponse]);

  return (
    <Flex
      centered
      width='100vw'
      height='100vh'
      backgroundColor={testConfig.contrastType === 'whiteOnBlack' ? 'black' : 'white'}
      onClick={() => processPatientClick()}
      style={{ cursor: 'none' }}
    >
      <FixationTargetVF2 coords={fixationTargetCoordinates} setupData={testConfig} />
      {testEnded && (
        <Text
          color={testConfig.contrastType === 'whiteOnBlack' ? '#fff' : '#000'}
          size='h1'
          style={{ marginTop: '10em', opacity: 0 }}
          animation={`${fadeIn} 2s 1s forwards`}
        >
          {`Thank you, the test is complete. Please await further instructions from your clinician.`}
        </Text>
      )}
      {showStimulus && <StimulusPointVF2 coords={stimulusCoordinates} color={testConfig.contrastType === 'whiteOnBlack' ? 'white' : 'black'} />}
    </Flex>
  );
};

export default VisualFieldTwo;
